import { useId } from "react";
import { Alert, Box, Button, Chip, CircularProgress, List, ListItem, ListItemButton, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import type { AgendaItem } from "@bw/shared/types/generated";
import { DateBadge } from "../../../common/components/DateBadge";
import { DenseRow } from "../../../common/components/DenseRow";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { visuallyHidden } from "../../../common/visuallyHidden";
import { usePlayerAgenda } from "../hooks/usePlayerAgenda";
import type { PlayerAgendaProps } from "../types";

export const SOURCE_LABELS: Record<AgendaItem["key"]["source"], string> = {
  facility: "Court bookings", program: "Classes", coaching: "Private lessons",
  league: "Leagues", tournament: "Tournaments", drop_in: "Drop-in", queue: "Queue and invitations",
  membership: "Memberships",
  community: "Community events",
};

export function timeLabel(value: string, zone?: string | null): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    timeZone: zone ?? "UTC", timeZoneName: "short",
  };
  try {
    return new Intl.DateTimeFormat(undefined, options).format(new Date(value));
  } catch {
    // Server and browser timezone databases can differ. Keep the real instant
    // and disclose UTC instead of crashing other healthy source sections.
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone: "UTC" }).format(new Date(value));
  }
}

/** The agenda window's last day, e.g. "Nov 30" (the server sets the window). */
function windowEndLabel(end: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(end));
}

export function PlayerAgenda({ variant }: PlayerAgendaProps) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    const returnUrl = `${location.pathname}${location.search}${location.hash}`;
    return <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
      <Typography color="text.secondary">Sign in to see your activity.</Typography>
      <Button component={RouterLink} to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} size="small">Sign in</Button>
    </Stack>;
  }
  return <AgendaBody key={user.uid} variant={variant} />;
}

function AgendaBody({ variant }: PlayerAgendaProps) {
  const limit = variant === "home" ? 3 : 20;
  const attention = usePlayerAgenda("attention", limit);
  const upcoming = usePlayerAgenda("upcoming", limit);
  if (variant === "home") return <HomeAgenda attention={attention} upcoming={upcoming} />;
  return <Stack spacing={2}>
    <AgendaSection mode="attention" query={attention} variant={variant} />
    <AgendaSection mode="upcoming" query={upcoming} variant={variant} />
  </Stack>;
}

function HomeAgenda({ attention, upcoming }: {
  attention: ReturnType<typeof usePlayerAgenda>;
  upcoming: ReturnType<typeof usePlayerAgenda>;
}) {
  const paused = [attention, upcoming].some((query) => query.fetchStatus === "paused");
  const loading = [attention, upcoming].some((query) =>
    !query.current && !query.isError && query.fetchStatus !== "paused");
  if (loading) return <Typography color="text.secondary" role="status">Loading your schedule…</Typography>;
  const seen = new Set<string>();
  const items = [...attention.items, ...upcoming.items].filter((item) => {
    const key = `${item.key.source}:${item.key.row_id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 3);
  const incomplete = paused || attention.isError || upcoming.isError || attention.partial || upcoming.partial;
  if (!items.length && !incomplete) return null;
  const headingId = "home-up-next-heading";
  return <Stack component="section" aria-labelledby={headingId} spacing={0.5}>
    <SectionHeader id={headingId} title="Up next" action={{ label: "Calendar", to: "/me/calendar", ariaLabel: "Open calendar" }} />
    {incomplete ? <Alert severity={paused ? "info" : "warning"} action={<Button color="inherit" size="small" onClick={() => {
      void attention.restart(); void upcoming.restart();
    }}>Retry</Button>}>{paused ? "You're offline. Your schedule will refresh when you're connected." : "Some schedule information is unavailable."}</Alert> : null}
    {items.length ? <List disablePadding>
      {items.map((item) => <UpNextRow key={`${item.key.source}:${item.key.row_id}`} item={item} />)}
    </List> : null}
  </Stack>;
}

function browserZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Source-local clock time. The zone is named whenever it isn't the viewer's
 * own (no source zone means UTC, which is always named).
 */
function clockLabel(value: string, zone?: string | null): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric", minute: "2-digit", timeZone: zone ?? "UTC",
    ...(zone && zone === browserZone() ? {} : { timeZoneName: "short" }),
  };
  try {
    return new Intl.DateTimeFormat(undefined, options).format(new Date(value));
  } catch {
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone: "UTC", timeZoneName: "short" }).format(new Date(value));
  }
}

/** "7:00 PM–8:00 PM" when the source gives an end, else the start alone. */
function whenLabel(item: AgendaItem): string | null {
  if (item.starts_at) {
    const start = clockLabel(item.starts_at, item.timezone);
    return item.ends_at ? `${start}–${clockLabel(item.ends_at, item.timezone)}` : start;
  }
  return item.deadline_at ? `Due ${clockLabel(item.deadline_at, item.timezone)}` : null;
}

/** A dense agenda row: date tile, title, time and place, then status. */
function UpNextRow({ item }: { item: AgendaItem }) {
  const at = item.starts_at ?? item.deadline_at;
  const when = whenLabel(item);
  const facts = [when, item.facility_name, item.resource_name].filter(Boolean).join(" · ");
  const status = item.status.replaceAll("_", " ");
  return <DenseRow
    to={item.action?.url}
    ariaLabel={item.action?.label}
    titleComponent="h3"
    leading={at ? <DateBadge at={at} timeZone={item.timezone ?? "UTC"} /> : null}
    title={item.title}
    subtitle={item.kind === "tournament_team_commitment"
      ? <><span>Team commitment</span>{facts ? ` · ${facts}` : null}</>
      : facts}
    trailing={<Chip size="small" variant="outlined" label={status} sx={{ textTransform: "capitalize" }} />}
  />;
}

function AgendaSection({ mode, query, variant }: PlayerAgendaProps & {
  mode: "attention" | "upcoming";
  query: ReturnType<typeof usePlayerAgenda>;
}) {
  const headingId = useId();
  const loaded = Boolean(query.current) && !query.isError && query.fetchStatus !== "paused";
  const empty = loaded && !query.items.length && !query.partial;
  return <Stack component="section" aria-labelledby={headingId} spacing={0.5}>
    {mode === "attention"
      ? <SectionHeader id={headingId} title="Needs attention" aside={empty ? "All clear" : undefined} />
      : <SectionHeader id={headingId} title="Upcoming" action={{ label: "Calendar", to: "/me/calendar", ariaLabel: "Open calendar" }} />}
    {query.fetchStatus === "paused" ? <Alert severity="info" role="status">Waiting for a connection to refresh activity.</Alert>
      : query.isError ? <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={() => void query.restart()}>Retry</Button>
      }>We couldn&apos;t load your activity.</Alert>
      : !query.current ? <Stack direction="row" spacing={1} role="status" sx={{ alignItems: "center", py: 1 }}>
        <CircularProgress size={18} /><Typography color="text.secondary">Loading activity…</Typography>
      </Stack> : <>
        {query.partial ? <Alert severity="warning" action={
          <Button color="inherit" size="small" onClick={() => void query.restart()}>Retry</Button>
        }>
          Some activity is unavailable{query.unavailable.length ? `: ${query.unavailable.map((source) => SOURCE_LABELS[source]).join(", ")}` : "."}
        </Alert> : null}
        {query.items.length ? <List disablePadding>
          {query.items.map((item) => <UpNextRow key={`${item.key.source}:${item.key.row_id}`} item={item} />)}
        </List> : empty && mode === "upcoming" ? <Stack spacing={1} sx={{ py: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {query.window ? `Nothing scheduled before ${windowEndLabel(query.window.end)}.` : "Nothing scheduled soon."}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
            <Button component={RouterLink} to="/play" size="small" variant="outlined" color="inherit" sx={{ borderRadius: 999 }}>Find somewhere to play</Button>
            <Button component={RouterLink} to="/compete/tournaments" size="small" variant="outlined" color="inherit" sx={{ borderRadius: 999 }}>Browse competitions</Button>
          </Stack>
        </Stack> : null}
        {variant === "activity" && query.hasNextPage && !query.partial ? <Button
          size="small" sx={{ alignSelf: "flex-start" }}
          onClick={() => { if (!query.isFetching) void query.fetchNextPage(); }}
        >Load more</Button> : null}
      </>}
  </Stack>;
}

/** Clock time in the page's disclosed timezone (the calendar names it once). */
function plainClock(value: string, zone?: string | null): string {
  const options: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", timeZone: zone ?? "UTC" };
  try {
    return new Intl.DateTimeFormat(undefined, options).format(new Date(value));
  } catch {
    return new Intl.DateTimeFormat(undefined, { ...options, timeZone: "UTC" }).format(new Date(value));
  }
}

/**
 * A calendar row: the start clock time as a leading column, then the title and
 * one facts line. The full start/end/deadline instants stay as <time> text.
 */
export function AgendaRow({ item, displayTimezone, titleComponent = "h3" }: {
  item: AgendaItem;
  displayTimezone?: string;
  /** "h4" when rows sit under a day heading. */
  titleComponent?: "h3" | "h4";
}) {
  const timezone = displayTimezone ?? item.timezone;
  const at = item.starts_at ?? item.deadline_at;
  const facts = [
    item.kind === "tournament_team_commitment" ? "Team commitment" : null,
    item.facility_name,
    item.resource_name,
  ].filter(Boolean).join(" · ");
  const content = <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, flex: 1, alignItems: "flex-start" }}>
    <Box aria-hidden sx={{ width: 52, flexShrink: 0, textAlign: "center", pt: 0.25 }}>
      {at ? <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, lineHeight: 1.2 }}>
        {item.starts_at ? plainClock(item.starts_at, timezone) : "Due"}
      </Typography> : null}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1, overflowWrap: "anywhere" }}>
      <Typography component={titleComponent} sx={{ m: 0, fontSize: "0.9375rem", fontWeight: 700 }}>{item.title}</Typography>
      <Typography variant="body2" color="text.secondary">
        <Box component="span" sx={{ textTransform: "capitalize" }}>{item.status.replaceAll("_", " ")}</Box>
        {facts ? ` · ${facts}` : null}
      </Typography>
      {/* The leading clock and the day heading show this; keep the exact
          instants for assistive tech. */}
      {item.starts_at ? <Typography variant="caption" component="p" sx={visuallyHidden}>
        <time dateTime={item.starts_at}>{timeLabel(item.starts_at, timezone)}</time>
        {item.ends_at ? <> – <time dateTime={item.ends_at}>{timeLabel(item.ends_at, timezone)}</time></> : null}
      </Typography> : null}
      {item.deadline_at ? <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
        Deadline: <time dateTime={item.deadline_at}>{timeLabel(item.deadline_at, timezone)}</time>
      </Typography> : null}
    </Box>
  </Stack>;
  return <ListItem disablePadding>
    {item.action ? <ListItemButton
      component={RouterLink}
      to={item.action.url}
      aria-label={item.action.label}
      sx={{ py: 1, px: { xs: 0.5, sm: 1 }, borderRadius: 2, alignItems: "flex-start" }}
    >
      {content}
    </ListItemButton> : <Stack sx={{ width: "100%", py: 1, px: { xs: 0.5, sm: 1 } }}>{content}</Stack>}
  </ListItem>;
}
